import React from "react";
import { Droppable } from "react-beautiful-dnd";

import KanbanCard from "./KanbanCard";
import { fmtBRL, tintFromColor } from "./tokens";

// Coluna/lane por variante. `stage` é o estágio real (id,name,color,kind).
// `count`/`total` são sobre leads NÃO filtrados; `cards` são os decorados visíveis.

const EmptyPlaceholder = ({ variant }) => {
  const styles = {
    sunlit: {
      border: "2px dashed #E0D6C2",
      borderRadius: 12,
      padding: "18px 10px",
      color: "#A79E8C",
      fontSize: 11.5,
    },
    funnel: {
      border: "1.5px dashed #C9D2DD",
      borderRadius: 8,
      padding: "16px 10px",
      color: "#98A2B3",
      fontSize: 11,
    },
    soft: {
      border: "2px dashed #DDD7E8",
      borderRadius: 14,
      padding: "18px 10px",
      color: "#B0A8C2",
      fontSize: 11.5,
    },
  };
  const s = styles[variant] || styles.funnel;
  return (
    <div
      style={{
        textAlign: "center",
        fontWeight: variant === "funnel" ? 600 : 700,
        ...s,
      }}
    >
      Arraste leads aqui
    </div>
  );
};

const KanbanColumn = ({
  variant,
  stage,
  count,
  total,
  cards,
  showMsg,
  onCardClick,
}) => {
  const color = stage.color || "#64748B";
  const isEmpty = cards.length === 0;

  // Wrapper externo por variante (fundo/borda da lane).
  const columnWrapper = {
    sunlit: {
      width: 268,
      flex: "none",
      background: "#F3EADA",
      borderRadius: 16,
      padding: 10,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      minHeight: 180,
    },
    funnel: {
      width: 266,
      flex: "none",
      background: tintFromColor(color),
      borderTop: `3px solid ${color}`,
      borderRadius: 10,
      padding: 10,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      minHeight: 170,
    },
    soft: {
      width: 264,
      flex: "none",
      display: "flex",
      flexDirection: "column",
      minHeight: 200,
    },
  }[variant];

  const header = renderHeader(variant, stage, color, count, total);

  return (
    <div style={columnWrapper}>
      {header}
      <Droppable droppableId={String(stage.id)}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: variant === "soft" ? 12 : variant === "sunlit" ? 10 : 8,
              flex: 1,
              minHeight: 40,
              borderRadius: 12,
              transition: "background .15s",
              background: snapshot.isDraggingOver
                ? "rgba(0,0,0,.04)"
                : "transparent",
            }}
          >
            {cards.map((card, index) => (
              <KanbanCard
                key={card.id}
                card={card}
                index={index}
                variant={variant}
                showMsg={showMsg}
                onClick={() => onCardClick(card.id)}
              />
            ))}
            {provided.placeholder}
            {isEmpty && <EmptyPlaceholder variant={variant} />}
          </div>
        )}
      </Droppable>
    </div>
  );
};

// Header da coluna por variante.
function renderHeader(variant, stage, color, count, total) {
  if (variant === "sunlit") {
    return (
      <div
        style={{
          background: color,
          borderRadius: 12,
          padding: "10px 14px 9px",
          color: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 800, fontSize: 14, flex: 1 }}>
            {stage.name}
          </span>
          <span
            style={{
              background: "rgba(255,255,255,.28)",
              borderRadius: 999,
              padding: "1px 9px",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {count}
          </span>
        </div>
        <div
          style={{
            fontSize: 11.5,
            opacity: 0.9,
            marginTop: 2,
            fontWeight: 800,
          }}
        >
          {fmtBRL(total)}
        </div>
      </div>
    );
  }

  if (variant === "soft") {
    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            padding: "0 2px",
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: ".08em",
              textTransform: "uppercase",
              color,
            }}
          >
            {stage.name}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#B0A8C2" }}>
            {count}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: "#8E86A0" }}>
            {fmtBRL(total)}
          </span>
        </div>
        <div
          style={{
            height: 3,
            borderRadius: 2,
            background: color,
            opacity: 0.35,
            margin: "7px 0 13px",
          }}
        />
      </>
    );
  }

  // funnel
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "2px 4px 4px",
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 2,
          background: color,
          flex: "none",
        }}
      />
      <span style={{ fontSize: 13, fontWeight: 700, color: "#1F2733" }}>
        {stage.name}
      </span>
      <span
        style={{
          background: "#fff",
          border: "1px solid #E2E7EE",
          borderRadius: 6,
          padding: "0 6px",
          fontSize: 11,
          fontWeight: 600,
          color: "#7A8496",
        }}
      >
        {count}
      </span>
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 11.5, fontWeight: 700, color }}>
        {fmtBRL(total)}
      </span>
    </div>
  );
}

export default KanbanColumn;
